import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import styles from "./SearchableDropdown.module.css";
import searchIcon from "../../public/assets/search-icon.svg";

const SearchableDropdown = forwardRef(({
  options = [],
  label,
  onChange,
  disabled = false,
  setFilteredList,
  onClear,
  onSearch,
  enableApiSearch = false,
}, ref) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const handleSelectOption = useCallback(
    (option) => {
      setSelectedOption(option);
      setIsOpen(false);
      setSearchText("");
      if (setFilteredList) {
        setFilteredList([option.fullData]);
      }
      if (onChange) {
        onChange(option.value, option.label, option);
      }
    },
    [onChange, setFilteredList]
  );

  const handleToggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleSearchInputChange = useCallback(
    (value) => {
      setSearchText(value);
      if (!isOpen) {
        setIsOpen(true);
      }

      // If API search is enabled and onSearch callback is provided
      if (enableApiSearch && onSearch && value.trim() !== "") {
        // Clear any existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set loading state
        setIsLoading(true);

        // Set up debounced API call (500ms delay)
        debounceTimerRef.current = setTimeout(async () => {
          try {
            await onSearch(value);
          } catch (error) {
            console.error("Error fetching search results:", error);
          } finally {
            setIsLoading(false);
          }
        }, 500);
      } else if (value.trim() === "") {
        setIsLoading(false);
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      }
    },
    [isOpen, enableApiSearch, onSearch]
  );

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchText("");
    }
  }, []);

  const handleInputChange = (e) => {
    e.stopPropagation();
    handleSearchInputChange(e.target.value);
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  const handleClearSearch = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    setSearchText("");
    setIsOpen(false);
    setIsLoading(false);
    setFilteredOptions(options);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (setFilteredList) {
      setFilteredList(options.map((opt) => opt.fullData));
    }
    if (onClear) {
      onClear();
    }
  };

  // Expose clearDropdown method to parent via ref
  useImperativeHandle(ref, () => ({
    clearDropdown: () => {
      setSelectedOption(null);
      setSearchText("");
      setIsOpen(false);
      setIsLoading(false);
      setFilteredOptions(options);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (setFilteredList) {
        setFilteredList(options.map((opt) => opt.fullData));
      }
      if (onClear) {
        onClear();
      }
    }
  }));

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    // Only do local filtering if API search is not enabled
    if (!enableApiSearch) {
      if (searchText !== "") {
        const filtered = options.filter((item) => {
          const searchLower = searchText.toLowerCase();
          return (
            item.label.toLowerCase().includes(searchLower) ||
            (item.email && item.email.toLowerCase().includes(searchLower)) ||
            (item.phone && item.phone.toLowerCase().includes(searchLower)) ||
            (item.accountNo &&
              item.accountNo.toString().toLowerCase().includes(searchLower))
          );
        });
        setFilteredOptions(filtered);
      } else {
        setFilteredOptions(options);
      }
    } else {
      // For API search, use the options passed from parent
      setFilteredOptions(options);
    }
  }, [searchText, options, enableApiSearch]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div ref={dropdownRef} className={styles.dropdownContainer}>
      <div
        className={`${styles.dropdownToggle} ${
          disabled ? styles.disabled : ""
        }`}
        onClick={handleToggleDropdown}
      >
        <span className={styles.searchIconWrapper}>
          <img
            src={searchIcon}
            alt="search"
            className={styles.searchIconImage}
          />
        </span>
        <input
          type="text"
          placeholder={selectedOption?.label || label}
          value={searchText}
          onChange={handleInputChange}
          onClick={handleInputClick}
          className={styles.searchInput}
          disabled={disabled}
        />
        {(searchText || selectedOption) && (
          <span className={styles.clearButton} onClick={handleClearSearch}>
            ×
          </span>
        )}
      </div>

      {isOpen && (
        <ul className={styles.dropdownMenu}>
          {isLoading ? (
            <li className={styles.loadingItem}>Loading...</li>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.value || index}
                onClick={() => handleSelectOption(option)}
                className={styles.dropdownItem}
              >
                <div className={styles.optionRow}>
                  <div className={styles.optionLabel}>{option.label}</div>
                  {(option.accountNo || option.phone || option.email) && (
                    <div className={styles.optionDetails}>
                      {option?.fullData?.accountNo ? (
                        <>
                          <span>Global Account No. </span>
                          <span>{option?.fullData?.accountNo}</span>
                        </>
                      ) : (
                        ""
                      )}
                      {/* // ) : ( */}
                      {/* //   <>
                      //     {option.email && <span>{option.email}</span>}
                      //     {option.email && option.phone && <span> | </span>}
                      //     {option.phone && <span>{option.phone}</span>}
                      //   </>
                      // )} */}
                    </div>
                  )}
                </div>
              </li>
            ))
          ) : (
            <li className={styles.noResults}>No results found</li>
          )}
        </ul>
      )}
    </div>
  );
});

SearchableDropdown.displayName = 'SearchableDropdown';

export default SearchableDropdown;
